import { useState, useEffect, useRef, FunctionComponent } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import classNames from 'classnames';
import { API_BASE_URL, credentials } from '../../config';
import styles from './styles.module.scss';

// Assets
import topVideo from '../../assets/topVideo.mp4';
import p1 from '../../assets/images/1-1600.webp';
import p2 from '../../assets/images/p2-1600.webp';
import p3 from '../../assets/images/p3-1600.webp';
import p4 from '../../assets/images/p4-1600.webp';
import p5 from '../../assets/images/p5-1600.webp';
import p7 from '../../assets/images/p7-1600.webp';
import insta from '../../assets/icons/instagram.svg';
import fb from '../../assets/icons/f.svg';
import tik from '../../assets/icons/tiktok.svg';
import yt from '../../assets/icons/yt.svg';

// Brand SVG Assets
import logoSvg from '../../assets/svg/logo.svg';
import logoMSvg from '../../assets/svg/logo-m.svg';
import starSvg from '../../assets/svg/star.svg';
import starVerticalSvg from '../../assets/svg/star-vertical.svg';
import starSmallSvg from '../../assets/svg/star-small.svg';

// Background Stars Constellation with all 3 star SVG variants
const BACKGROUND_STARS = [
	// Hero Video & Header Area
	{ top: '2%', left: '10%', width: 18, height: 18, delay: '0.2s', rotate: '0deg', src: starSvg },
	{ top: '4%', left: '85%', width: 18, height: 42, delay: '1.4s', rotate: '12deg', src: starVerticalSvg },
	{ top: '7%', left: '25%', width: 14, height: 14, delay: '2.7s', rotate: '-8deg', src: starSmallSvg },
	{ top: '10%', left: '72%', width: 16, height: 38, delay: '0.6s', rotate: '15deg', src: starVerticalSvg },
	{ top: '13%', left: '92%', width: 22, height: 22, delay: '0.9s', rotate: '25deg', src: starSvg },
	{ top: '16%', left: '6%', width: 15, height: 35, delay: '3.1s', rotate: '-10deg', src: starVerticalSvg },
	{ top: '19%', left: '80%', width: 14, height: 14, delay: '1.8s', rotate: '18deg', src: starSmallSvg },
	{ top: '22%', left: '18%', width: 24, height: 24, delay: '0.5s', rotate: '5deg', src: starSvg },
	{ top: '25%', left: '88%', width: 16, height: 38, delay: '2.3s', rotate: '-15deg', src: starVerticalSvg },
	{ top: '27%', left: '45%', width: 12, height: 12, delay: '3.8s', rotate: '-5deg', src: starSmallSvg },

	// Concept / Manifesto Area
	{ top: '30%', left: '8%', width: 15, height: 15, delay: '1.2s', rotate: '20deg', src: starSmallSvg },
	{ top: '33%', left: '94%', width: 20, height: 20, delay: '3.6s', rotate: '-5deg', src: starSvg },
	{ top: '37%', left: '5%', width: 18, height: 44, delay: '0.8s', rotate: '8deg', src: starVerticalSvg },
	{ top: '40%', left: '82%', width: 14, height: 14, delay: '2.5s', rotate: '-18deg', src: starSmallSvg },
	{ top: '44%', left: '16%', width: 22, height: 22, delay: '1.6s', rotate: '30deg', src: starSvg },
	{ top: '47%', left: '90%', width: 16, height: 38, delay: '4.0s', rotate: '-12deg', src: starVerticalSvg },

	// Stats Area
	{ top: '51%', left: '7%', width: 14, height: 14, delay: '2.1s', rotate: '0deg', src: starSmallSvg },
	{ top: '54%', left: '50%', width: 20, height: 20, delay: '0.4s', rotate: '18deg', src: starSvg },
	{ top: '57%', left: '93%', width: 18, height: 42, delay: '3.3s', rotate: '10deg', src: starVerticalSvg },
	{ top: '61%', left: '12%', width: 15, height: 15, delay: '1.5s', rotate: '-22deg', src: starSmallSvg },
	{ top: '64%', left: '86%', width: 24, height: 24, delay: '2.9s', rotate: '12deg', src: starSvg },
	{ top: '67%', left: '4%', width: 16, height: 38, delay: '0.7s', rotate: '-8deg', src: starVerticalSvg },

	// Video & Stories Area
	{ top: '71%', left: '91%', width: 14, height: 14, delay: '3.8s', rotate: '25deg', src: starSmallSvg },
	{ top: '74%', left: '16%', width: 22, height: 22, delay: '1.1s', rotate: '-15deg', src: starSvg },
	{ top: '78%', left: '80%', width: 18, height: 42, delay: '2.6s', rotate: '14deg', src: starVerticalSvg },
	{ top: '81%', left: '7%', width: 15, height: 15, delay: '0.3s', rotate: '-5deg', src: starSmallSvg },
	{ top: '84%', left: '94%', width: 20, height: 20, delay: '3.0s', rotate: '20deg', src: starSvg },
	{ top: '87%', left: '22%', width: 16, height: 36, delay: '1.7s', rotate: '-10deg', src: starVerticalSvg },

	// Newsletter & Footer Area
	{ top: '90%', left: '87%', width: 14, height: 14, delay: '2.2s', rotate: '15deg', src: starSmallSvg },
	{ top: '93%', left: '9%', width: 22, height: 22, delay: '0.9s', rotate: '-20deg', src: starSvg },
	{ top: '96%', left: '74%', width: 18, height: 42, delay: '3.5s', rotate: '8deg', src: starVerticalSvg },
	{ top: '98%', left: '36%', width: 15, height: 15, delay: '1.4s', rotate: '-12deg', src: starSmallSvg },
];

// Newsletter Form with Tickets Website Logic (Segment 13)
const NewsletterForm = () => {
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		city: '',
		email: '',
	});
	const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
	const [errorMessage, setErrorMessage] = useState('');

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		if (status === 'error') setStatus('idle');
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const emailRegex = /\S+@\S+\.\S+/;
		if (!formData.email || !emailRegex.test(formData.email)) {
			setStatus('error');
			setErrorMessage('Te rugam sa introduci o adresa de email valida.');
			return;
		}

		setStatus('loading');
		setErrorMessage('');

		const payload = {
			email: formData.email.trim(),
			firstName: formData.firstName.trim(),
			lastName: formData.lastName.trim(),
			city: formData.city.trim(),
		};

		try {
			const res = await axios.post(`${API_BASE_URL}/segments/13/subscribe`, payload, {
				headers: {
					'Content-Type': 'application/json',
				},
			});

			if (res.status >= 200 && res.status < 300) {
				setStatus('success');
				try {
					localStorage.setItem('notifySubscriberEmail', formData.email.trim());
					localStorage.setItem(
						'notifySubscriberName',
						`${formData.firstName} ${formData.lastName}`.trim()
					);
					localStorage.setItem('notifySubscriberCity', formData.city.trim());
				} catch (err) {
					// Ignore storage errors
				}

				// Analytics tracking
				if (typeof window !== 'undefined') {
					(window as any).dataLayer = (window as any).dataLayer || [];
					(window as any).dataLayer.push({
						event: 'newsletter_signup',
						event_name: 'newsletter_signup',
						city: formData.city.trim(),
						email: formData.email.trim(),
					});

					if (typeof (window as any).gtag === 'function') {
						(window as any).gtag('event', 'generate_lead', {
							event_category: 'Newsletter',
							event_label: formData.city.trim(),
							city: formData.city.trim(),
						});
					}

					if (typeof (window as any).fbq === 'function') {
						(window as any).fbq('track', 'Lead', {
							content_name: 'Newsletter Lead',
							city: formData.city.trim(),
							email: formData.email.trim(),
						});
					}
				}
			} else {
				throw new Error('Eroare la inregistrare');
			}
		} catch (err: any) {
			console.error('Newsletter error:', err);
			setStatus('error');
			setErrorMessage('A aparut o eroare la inregistrare. Te rugam sa incerci din nou.');
		}
	};

	return (
		<form onSubmit={handleSubmit} className={styles.newsletterForm} noValidate>
			<div className={styles.inputGrid}>
				<div>
					<label htmlFor="redesign-firstname" className={styles.visuallyHidden}>
						Prenume
					</label>
					<input
						id="redesign-firstname"
						type="text"
						name="firstName"
						value={formData.firstName}
						onChange={handleChange}
						placeholder="Prenume (ex: Ion)"
						aria-label="Prenume"
						autoComplete="given-name"
						required
						disabled={status === 'loading' || status === 'success'}
					/>
				</div>
				<div>
					<label htmlFor="redesign-lastname" className={styles.visuallyHidden}>
						Nume
					</label>
					<input
						id="redesign-lastname"
						type="text"
						name="lastName"
						value={formData.lastName}
						onChange={handleChange}
						placeholder="Nume (ex: Popescu)"
						aria-label="Nume"
						autoComplete="family-name"
						required
						disabled={status === 'loading' || status === 'success'}
					/>
				</div>
				<div>
					<label htmlFor="redesign-city" className={styles.visuallyHidden}>
						Oras
					</label>
					<input
						id="redesign-city"
						type="text"
						name="city"
						value={formData.city}
						onChange={handleChange}
						placeholder="Oras (ex: Cluj-Napoca)"
						aria-label="Oras"
						autoComplete="address-level2"
						required
						disabled={status === 'loading' || status === 'success'}
					/>
				</div>
				<div>
					<label htmlFor="redesign-email" className={styles.visuallyHidden}>
						Email
					</label>
					<input
						id="redesign-email"
						type="email"
						name="email"
						value={formData.email}
						onChange={handleChange}
						placeholder="Email (ex: user@example.com)"
						aria-label="Adresa ta de email"
						autoComplete="email"
						required
						disabled={status === 'loading' || status === 'success'}
					/>
				</div>
			</div>

			<button
				type="submit"
				className={styles.goldButton}
				disabled={status === 'loading' || status === 'success'}
				style={{ width: '100%', justifyContent: 'center', marginTop: '1rem' }}
				aria-label="Aboneaza-te la newsletter"
			>
				{status === 'loading' ? (
					<span>Se trimite...</span>
				) : status === 'success' ? (
					<span>✓ Inregistrat cu succes</span>
				) : (
					<>
						<span>Aboneaza-te acum</span>
						<span>→</span>
					</>
				)}
			</button>

			<div aria-live="polite" aria-atomic="true">
				{status === 'success' && (
					<div role="status" className={styles.msgSuccess}>
						✓ Te-am inregistrat cu succes! Te vom notifica primul la lansare.
					</div>
				)}
				{status === 'error' && (
					<div role="alert" className={styles.msgError}>
						{errorMessage || 'A aparut o eroare.'}
					</div>
				)}
			</div>
		</form>
	);
};

interface YoutubeVideoItem {
	id: string;
	title: string;
}

const DEFAULT_YOUTUBE_VIDEOS: YoutubeVideoItem[] = [
	{
		id: 'HXooqlhuams',
		title: 'Maneliada 2025 | Cu Capul In Nori Aftermovie',
	},
	{
		id: 'A4FZaMJuFQM',
		title: 'Maneliada 2024 | Turneul "Chef de Chef" Aftermovie',
	},
	{
		id: 'y8ozpIJdot0',
		title: 'Maneliada 2024 | "Toate Diamantele" Official Aftermovie',
	},
	{
		id: 'RbaJIe1CME8',
		title: 'Maneliada 2023 | Turneu Primavara Official Aftermovie',
	},
	{
		id: '06bIW8mXj8s',
		title: 'Maneliada 2022 | Aftermovie Autumn/Winter Tour',
	},
];

const Home: FunctionComponent = () => {
	const [isQuoteIlluminated, setIsQuoteIlluminated] = useState(false);
	const [showScrollIndicator, setShowScrollIndicator] = useState(true);
	const [youtubeVideos, setYoutubeVideos] = useState<YoutubeVideoItem[]>(DEFAULT_YOUTUBE_VIDEOS);
	const [activeYtIndex, setActiveYtIndex] = useState(0);
	const quoteRef = useRef<HTMLHeadingElement>(null);
	const heroVideoRef = useRef<HTMLVideoElement>(null);

	// Ensure hero video autoplays reliably on iOS & mobile browsers
	useEffect(() => {
		if (heroVideoRef.current) {
			heroVideoRef.current.muted = true;
			heroVideoRef.current.defaultMuted = true;
			heroVideoRef.current.setAttribute('muted', '');
			heroVideoRef.current.setAttribute('playsinline', '');
			heroVideoRef.current.setAttribute('webkit-playsinline', '');
			const playPromise = heroVideoRef.current.play();
			if (playPromise !== undefined) {
				playPromise.catch(() => {
					// Fallback if browser blocks unprompted playback
				});
			}
		}
	}, []);

	// Load YouTube Playlist with titles dynamically
	useEffect(() => {
		const loadYoutubePlaylist = async () => {
			try {
				const res = await axios.get(
					`https://youtube.googleapis.com/youtube/v3/playlistItems?part=snippet,contentDetails&maxResults=25&playlistId=${credentials.playlistId}&key=${credentials.youtubeApiKey}`,
					{
						headers: {
							Accept: 'application/json',
						},
					}
				);
				if (res.data?.items?.length) {
					const items: YoutubeVideoItem[] = res.data.items
						.filter((item: any) => {
							const title = item.snippet?.title;
							return title && title !== 'Private video' && title !== 'Deleted video';
						})
						.map((item: any) => ({
							id: item.contentDetails?.videoId || item.snippet?.resourceId?.videoId,
							title: item.snippet?.title || 'Video',
						}))
						.filter((v: YoutubeVideoItem) => Boolean(v.id));

					if (items.length > 0) {
						setYoutubeVideos(items.reverse());
					}
				}
			} catch (e) {
				console.error('Could not fetch YouTube playlist:', e);
			}
		};
		loadYoutubePlaylist();
	}, []);

	// IntersectionObserver for Kinetic Quote Illumination & Passive scroll indicator
	useEffect(() => {
		const handleScroll = () => {
			setShowScrollIndicator(window.scrollY <= 70);
		};

		window.addEventListener('scroll', handleScroll, { passive: true });
		handleScroll();

		// Use IntersectionObserver for smooth GPU-friendly viewport detection without layout thrashing
		let observer: IntersectionObserver | null = null;
		if (quoteRef.current && typeof IntersectionObserver !== 'undefined') {
			observer = new IntersectionObserver(
				([entry]) => {
					setIsQuoteIlluminated(entry.isIntersecting);
				},
				{
					threshold: 0.35,
					rootMargin: '-5% 0px -5% 0px',
				}
			);
			observer.observe(quoteRef.current);
		}

		return () => {
			window.removeEventListener('scroll', handleScroll);
			if (observer) observer.disconnect();
		};
	}, []);

	return (
		<div className={styles.pageWrapper}>
			{/* Ambient Warm Golden Lighting Orbs */}
			<div className={styles.ambientGlowTop} />
			<div className={styles.ambientGlowMid} />

			{/* Background Star Constellation Field */}
			<div className={styles.starsContainer}>
				{BACKGROUND_STARS.map((star, idx) => (
					<div
						key={idx}
						className={styles.starItem}
						style={{
							top: star.top,
							left: star.left,
							width: star.width,
							height: star.height,
							animationDelay: star.delay,
							transform: `rotate(${star.rotate})`,
						}}
					>
						<img
							src={star.src}
							alt=""
							width={star.width}
							height={star.height}
							className={styles.starAssetImg}
						/>
					</div>
				))}
			</div>

			{/* Floating Header */}
			<header className={styles.header}>
				<a href="#hero" className={styles.logoLink} aria-label="Maneliada Home">
					<img src={logoSvg} alt="Maneliada" height="58" />
				</a>

				<div className={styles.headerActions}>
					<Link to="/ambassador" className={styles.ambassadorLink}>
						Portal Ambasador
					</Link>
					<a
						href="https://bilete.maneliada.ro"
						target="_blank"
						rel="noreferrer noopener"
						className={styles.goldButton}
						style={{ padding: '0.6rem 1.6rem', fontSize: '0.8rem' }}
					>
						<span>Bilete</span>
						<span>↗</span>
					</a>
				</div>
			</header>

			{/* Hero Section */}
			<section id="hero" className={styles.heroSection}>
				<video
					ref={heroVideoRef}
					autoPlay
					loop
					muted
					playsInline
					preload="auto"
					className={styles.videoBg}
				>
					<source src={topVideo} type="video/mp4" />
				</video>
				<div className={styles.videoOverlay} />

				{/* Ambient Brand Watermark */}
				<div className={styles.heroWatermark}>
					<img src={logoMSvg} alt="" width="480" height="645" />
				</div>

				<h1 className={styles.heroTitle}>
					O EXPERIENTA, <br />
					NU DOAR O PETRECERE
				</h1>

				<p className={styles.heroSubtitle}>
					Traieste momentul si bucura-te de <strong>Babi Minune</strong> si <strong>Fred Again..</strong> in acelasi timp.
				</p>

				<div className={styles.ctaGroup}>
					<a
						href="https://bilete.maneliada.ro"
						target="_blank"
						rel="noreferrer noopener"
						className={styles.goldButton}
					>
						<span>Cumpara Bilete</span>
						<img src={starSvg} alt="" width="16" height="16" />
					</a>
				</div>

				{/* Fixed Scroll Indicator */}
				<a
					href="#concept"
					className={classNames(styles.scrollDownIndicator, {
						[styles.hidden]: !showScrollIndicator,
					})}
					aria-label="Scroll la detalii"
				>
					<span>DESCOPERA</span>
					<div className={styles.mouseIcon}>
						<div className={styles.mouseDot} />
					</div>
				</a>
			</section>

			{/* Manifesto Concept Text (Kinetic Illumination) */}
			<section id="concept" className={styles.conceptSection}>
				<div className={styles.conceptInner}>
					<span className={styles.sectionEyebrow}>
						<img src={starSvg} alt="" width="13" height="13" className={styles.eyebrowStar} />
						<span>Conceptul Nostru</span>
					</span>
					<h2
						ref={quoteRef}
						className={classNames(styles.conceptQuote, {
							[styles.illuminated]: isQuoteIlluminated,
						})}
					>
						“O seara in care manelele vechi si cele noi se intalnesc cu ritmurile electronice.”
					</h2>
					<p className={styles.conceptSubtext}>
						Un fusion autentic intre energia clasica de chef si productia moderna de festival.
					</p>
				</div>
			</section>

			{/* Floating Scattered Stats Section */}
			<section className={styles.statsSection}>
				<div className={styles.statsGrid}>
					{/* Stat 1: 90% Sold Out */}
					<div className={classNames(styles.statCard, styles.floatCard1)}>
						<div className={styles.statNumber}>90%</div>
						<div className={styles.statLabel}>
							<img src={starSmallSvg} alt="" width="11" height="11" />
							<span>Sold Out</span>
						</div>
					</div>

					{/* Stat 2: 1k+ Ore */}
					<div className={classNames(styles.statCard, styles.floatCard2)}>
						<div className={styles.statNumber}>1k+</div>
						<div className={styles.statLabel}>
							<img src={starSmallSvg} alt="" width="11" height="11" />
							<span>Ore de Muzica</span>
						</div>
					</div>

					{/* Stat 3: 40k+ Petrecareti */}
					<div className={classNames(styles.statCard, styles.floatCard3)}>
						<div className={styles.statNumber}>40k+</div>
						<div className={styles.statLabel}>
							<img src={starSmallSvg} alt="" width="11" height="11" />
							<span>Petrecareti</span>
						</div>
					</div>

					{/* Stat 4: 12 Orase */}
					<div className={classNames(styles.statCard, styles.floatCard4)}>
						<div className={styles.statNumber}>12</div>
						<div className={styles.statLabel}>
							<img src={starSmallSvg} alt="" width="11" height="11" />
							<span>Orase in RO &amp; Strainatate</span>
						</div>
					</div>
				</div>
			</section>

			{/* YouTube Playlist Theater Section */}
			<section id="video" className={styles.videoSection}>
				<div className={styles.sectionHeader}>
					<span className={styles.sectionEyebrow}>
						<img src={starSvg} alt="" width="13" height="13" className={styles.eyebrowStar} />
						<span>Atmosfera de la Fata Locului</span>
					</span>
					<h2>FIECARE SEARA ESTE DIFERITA</h2>
				</div>

				<div className={styles.theaterContainer}>
					{youtubeVideos.length > 1 && (
						<button
							type="button"
							className={classNames(styles.sideArrow, styles.sideArrowPrev)}
							onClick={() =>
								setActiveYtIndex((prev) => (prev > 0 ? prev - 1 : youtubeVideos.length - 1))
							}
							aria-label="Previous video"
						>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
								<polyline points="15 18 9 12 15 6" />
							</svg>
						</button>
					)}

					<div className={styles.theaterCard}>
						<iframe
							key={youtubeVideos[activeYtIndex]?.id || DEFAULT_YOUTUBE_VIDEOS[0].id}
							src={`https://www.youtube.com/embed/${youtubeVideos[activeYtIndex]?.id || DEFAULT_YOUTUBE_VIDEOS[0].id}?autoplay=1&mute=1&playsinline=1&enablejsapi=1&rel=0`}
							title={youtubeVideos[activeYtIndex]?.title || "Maneliada YouTube Video Player"}
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
							allowFullScreen
						/>
					</div>

					{youtubeVideos.length > 1 && (
						<button
							type="button"
							className={classNames(styles.sideArrow, styles.sideArrowNext)}
							onClick={() =>
								setActiveYtIndex((prev) => (prev < youtubeVideos.length - 1 ? prev + 1 : 0))
							}
							aria-label="Next video"
						>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
								<polyline points="9 18 15 12 9 6" />
							</svg>
						</button>
					)}
				</div>

				{/* Theater Video Pagination Dots */}
				{youtubeVideos.length > 1 && (
					<div className={styles.paginationDots} role="tablist" aria-label="Videoclipuri Maneliada">
						{youtubeVideos.map((video, idx) => (
							<button
								key={video.id || idx}
								type="button"
								role="tab"
								aria-selected={idx === activeYtIndex}
								aria-label={`Videoclipul ${idx + 1}: ${video.title}`}
								className={classNames(styles.dotItem, {
									[styles.activeDot]: idx === activeYtIndex,
								})}
								onClick={() => setActiveYtIndex(idx)}
							/>
						))}
					</div>
				)}
			</section>

			{/* 3D Tilt Gallery & Authentic Quotes */}
			<section id="galerie" className={styles.gallerySection}>
				<div className={styles.sectionHeader}>
					<span className={styles.sectionEyebrow}>
						<img src={starSvg} alt="" width="13" height="13" className={styles.eyebrowStar} />
						<span>Haide Sus Pe Mese</span>
					</span>
					<h2>NU TE-AM CONVINS INCA?</h2>
					<p>Hai sa-ti aratam cum ne distram la fiecare editie.</p>
				</div>

				<div className={styles.cardsGrid}>
					{/* Testimonial 1 */}
					<div className={styles.tiltCard}>
						<div className={styles.imgWrap}>
							<img src={p1} alt="Maneliada Party" className={styles.coverImg} />
							<div className={styles.cityBadge}>
								<img src={starSmallSvg} alt="" width="10" height="10" className={styles.cityBadgeStar} />
								<span>Iasi</span>
							</div>
						</div>
						<div className={styles.cardContent}>
							<p className={styles.quoteText}>“N-ai cum sa nu-ti placa Maneliada”</p>
							<span className={styles.authorText}>— Ionut Bo$$u</span>
						</div>
					</div>

					{/* Testimonial 2 */}
					<div className={styles.tiltCard}>
						<div className={styles.imgWrap}>
							<img src={p2} alt="Maneliada Party" className={styles.coverImg} />
							<div className={styles.cityBadge}>
								<img src={starSmallSvg} alt="" width="10" height="10" className={styles.cityBadgeStar} />
								<span>Sibiu</span>
							</div>
						</div>
						<div className={styles.cardContent}>
							<p className={styles.quoteText}>“Maneliada e perfecta asa cum este”</p>
							<span className={styles.authorText}>— Horia Fantastik</span>
						</div>
					</div>

					{/* Testimonial 3 */}
					<div className={styles.tiltCard}>
						<div className={styles.imgWrap}>
							<img src={p3} alt="Maneliada Party" className={styles.coverImg} />
							<div className={styles.cityBadge}>
								<img src={starSmallSvg} alt="" width="10" height="10" className={styles.cityBadgeStar} />
								<span>Bucuresti</span>
							</div>
						</div>
						<div className={styles.cardContent}>
							<p className={styles.quoteText}>“Mama ce tari sunt mascatii de la Electro Manele, mi-au schimbat viata”</p>
							<span className={styles.authorText}>— Bogdan</span>
						</div>
					</div>

					{/* Testimonial 4 */}
					<div className={styles.tiltCard}>
						<div className={styles.imgWrap}>
							<img src={p4} alt="Maneliada Party" className={styles.coverImg} />
							<div className={styles.cityBadge}>
								<img src={starSmallSvg} alt="" width="10" height="10" className={styles.cityBadgeStar} />
								<span>Cluj-Napoca</span>
							</div>
						</div>
						<div className={styles.cardContent}>
							<p className={styles.quoteText}>“Am participat, am fost, mai merg!”</p>
							<span className={styles.authorText}>— Zana Paula</span>
						</div>
					</div>

					{/* Testimonial 5 */}
					<div className={styles.tiltCard}>
						<div className={styles.imgWrap}>
							<img src={p5} alt="Maneliada Party" className={styles.coverImg} />
							<div className={styles.cityBadge}>
								<img src={starSmallSvg} alt="" width="10" height="10" className={styles.cityBadgeStar} />
								<span>Bucuresti</span>
							</div>
						</div>
						<div className={styles.cardContent}>
							<p className={styles.quoteText}>“Sunteti smekerosi 😎”</p>
							<span className={styles.authorText}>— Sefa Andreea</span>
						</div>
					</div>

					{/* Testimonial 6 */}
					<div className={styles.tiltCard}>
						<div className={styles.imgWrap}>
							<img src={p7} alt="Maneliada Party" className={styles.coverImg} />
							<div className={styles.cityBadge}>
								<img src={starSmallSvg} alt="" width="10" height="10" className={styles.cityBadgeStar} />
								<span>Timisoara</span>
							</div>
						</div>
						<div className={styles.cardContent}>
							<p className={styles.quoteText}>“Mi-a placut tot, va ador!!”</p>
							<span className={styles.authorText}>— Andreea VTM</span>
						</div>
					</div>
				</div>
			</section>

			{/* Newsletter Section */}
			<section className={styles.newsletterSection}>
				<div className={styles.newsletterCard}>
					<span className={styles.sectionEyebrow}>
						<img src={starSvg} alt="" width="13" height="13" className={styles.eyebrowStar} />
						<span>Fii Primul Care Afla</span>
					</span>
					<h3>VREI SA FII TANAR ADECVAT?</h3>
					<p>
						Aboneaza-te la newsletter-ul nostru pentru a fi primul care afla cand are loc urmatoarea Maneliada in orasul tau. Cine stie, poate primesti si un voucher!
					</p>

					<NewsletterForm />
				</div>
			</section>

			{/* Clean Footer */}
			<footer className={styles.footer}>
				<div className={styles.footerInner}>
					<div className={styles.brandPart}>
						<img src={logoSvg} alt="Maneliada" height="34" />
						<span className={styles.copyright}>© 2021 - {new Date().getFullYear()} MANELIADA</span>
					</div>

					<div className={styles.linksPart}>
						<a
							href="https://bilete.maneliada.ro"
							target="_blank"
							rel="noreferrer noopener"
							className={styles.ticketsHighlight}
						>
							Bilete Oficiale ↗
						</a>
						<a
							href="https://maneliada-termeni.s3.eu-west-1.amazonaws.com/politica-de-confidentialitate.pdf"
							target="_blank"
							rel="noreferrer noopener"
						>
							Politica de Confidentialitate
						</a>
						<a
							href="https://maneliada-termeni.s3.eu-west-1.amazonaws.com/termeni-si-conditii-Maneliada-2024.pdf"
							target="_blank"
							rel="noreferrer noopener"
						>
							Termeni si Conditii
						</a>
						<Link to="/ambassador">Portal Ambasador</Link>
					</div>

					<div className={styles.socialPart}>
						<a href="https://facebook.com/maneliada" target="_blank" rel="noreferrer noopener" aria-label="Facebook">
							<img src={fb} alt="Facebook" />
						</a>
						<a href="https://instagram.com/maneliada" target="_blank" rel="noreferrer noopener" aria-label="Instagram">
							<img src={insta} alt="Instagram" />
						</a>
						<a href="https://tiktok.com/@maneliada_ofc" target="_blank" rel="noreferrer noopener" aria-label="TikTok">
							<img src={tik} alt="TikTok" />
						</a>
						<a href="https://www.youtube.com/@maneliadatv" target="_blank" rel="noreferrer noopener" aria-label="YouTube">
							<img src={yt} alt="YouTube" />
						</a>
					</div>
				</div>
			</footer>
		</div>
	);
};

export default Home;
